import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { ToggleComponent } from '../toggle/toggle.component';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { CommonService } from '../../services/common.service';
import { FilterService } from '../../services/filter.service';
import { LabelService } from '../../services/label.service';
import { Filters, LabelTypes } from '../../shared/label.types';
import { CustomInputType } from '../../shared/types';
import {
  CUSTOMER_SENTIMENT_COLUMNS,
  CUSTOMER_SENTIMENT_PREDEFINED_VALUES,
} from '../../shared/constant';
import { MatMenuModule } from '@angular/material/menu';
import { ClassNamePipe } from '../../pipes/class-name.pipe';
import { MatTooltip } from '@angular/material/tooltip';
import { TooltipPipe } from '../custom-select/tooltip.pipe';

@Component({
  selector: 'multi-selector',
  standalone: true,
  imports: [
    CommonModule,
    MatSlideToggleModule,
    FormsModule,
    ToggleComponent,
    MatMenuModule,
    ClassNamePipe,
    MatTooltip,
    TooltipPipe,
  ],
  templateUrl: './multi-selector.component.html',
  styleUrl: './multi-selector.component.scss',
})
export class MultiSelectorComponent implements OnChanges {
  constructor(
    public commonService: CommonService,
    public filterService: FilterService,
    private labelService: LabelService
  ) {}

  @Input() inputText: string | string[] = [''];
  @Input() openAbove: boolean = false;
  @Output() onInputChange = new EventEmitter<string[]>();
  @Input() columnName: string = '';
  @Input() customInputType: CustomInputType = CustomInputType.string;
  @Input() dropDownWidth: string = '300px';

  isOptionSelected = false;
  dropdownOpened: boolean = false;
  searchQuery: string = '';
  selectedOptions: string[] = [];
  primaryTopics: { label: string; tempIsSelected: boolean }[] = [];
  filteredCount: number = 0;
  topicsLabels: Filters = {} as Filters;
  selectedOptionText: string = this.topicsLabels.select_an_option;

  /**
   * When the parent clears selection (e.g. user changes operator "is" ↔ "is not"),
   * sync predefined list UI for all columns using this component: Primary Topic,
   * Agent Username, Intent, CallerType, CallResolution, JourneyMoment, Customer Sentiment, etc.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputText'] && !changes['inputText'].firstChange && this.primaryTopics.length > 0) {
      this.syncSelectionFromInput();
    }
  }

  private syncSelectionFromInput(): void {
    const inputArray = Array.isArray(this.inputText) ? this.inputText : [this.inputText].filter(Boolean);
    this.primaryTopics.forEach((topic) => {
      topic.tempIsSelected = inputArray.includes(topic.label);
    });
    this.updateSelectedOptions();
  }

  ngOnInit() {
    this.labelService.getLabelData().subscribe((data: LabelTypes) => {
      this.topicsLabels = data?.filter ?? {};
    });

    this.filterService.getAllFilters().subscribe((filters) => {
      // Customer Sentiment At Start/End: use predefined values (Positive, Neutral, Negative), not from backend
      if (CUSTOMER_SENTIMENT_COLUMNS.includes(this.columnName as (typeof CUSTOMER_SENTIMENT_COLUMNS)[number])) {
        const inputArray = Array.isArray(this.inputText) ? this.inputText : [this.inputText].filter(Boolean);
        this.primaryTopics = [...CUSTOMER_SENTIMENT_PREDEFINED_VALUES].map((label) => ({
          label,
          tempIsSelected: inputArray.includes(label),
        }));
        this.updateSelectedOptions();
        this.updateFiltersWithPrimaryTopics();
        return;
      }

      // Define a mapping from column names to API filter keys (predefined list from insights API)
      const filterKeyMap: Record<string, string> = {
        PrimaryTopic: 'primarytopic',
        Topics: 'TopicsClustered',
        TopicsClustered: 'TopicsClustered',
        Intent: 'intent',
        CallerType: 'callertype',
        CallResolution: 'callresolution',
        JourneyMoment: 'journeymoment',
        Agent_Username: 'agent_username',
        Queue_Name: 'queue_name',
      };

      // Extract filter key from the map
      const filterKey = filterKeyMap[this.columnName] ?? '';

      // Process the filter list based on the filter key
      let filterListPrimaryTopics: string[] = (filters.filters[filterKey] as string[]) ?? [''];

      this.primaryTopics = filterListPrimaryTopics
        .filter((topic) => topic && typeof topic === 'string' && topic.trim() !== '')
        .map((topic) => ({
          label: topic,
          tempIsSelected: this.inputText.includes(topic),
        }));

      // Common post-processing functions
      this.updateSelectedOptions();
      this.updateFiltersWithPrimaryTopics();
    });
  }

  updateFiltersWithPrimaryTopics(): void {}

  handleButtonClick(event: Event): void {
    this.dropdownOpened = !this.dropdownOpened;
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  onMatMenuClosed() {}

  onOutsideClick(): void {
    this.dropdownOpened = false;
  }

  handleItemSelect(item: { label: string; tempIsSelected: boolean }): void {
    item.tempIsSelected = !item.tempIsSelected;
    this.updateSelectedOptions();
  }

  updateSelectedOptions(): void {
    this.selectedOptions = this.primaryTopics.filter((f) => f.tempIsSelected).map((f) => f.label);
    if (this.onInputChange) {
      this.onInputChange.emit(this.selectedOptions);
    }
    this.updateSelectedOptionText();
  }

  onValueChange(state: 'on' | 'off', item: { label: string; tempIsSelected: boolean }): void {
    item.tempIsSelected = state === 'on';
    this.updateSelectedOptions();
  }

  updateSelectedOptionText(): void {
    const selectedCount = this.selectedOptions.length;
    if (selectedCount === 0) {
      this.selectedOptionText = 'Select an option';
      this.isOptionSelected = false;
    } else if (selectedCount === 1) {
      this.selectedOptionText = this.selectedOptions[0];
      this.isOptionSelected = true;
    } else {
      this.selectedOptionText = `${selectedCount} options selected`;
      this.isOptionSelected = true;
    }
  }

  get filteredTopics() {
    const query = this.searchQuery.trim().toLowerCase();

    // Filter topics based on the search query
    let filtered = this.primaryTopics.filter((topic) => topic.label.toLowerCase().indexOf(query) !== -1);

    // For Customer Sentiment (Start/End), keep fixed order so each toggle stays bound to the correct option.
    // Sorting by selection would reorder the list and, without trackBy, can cause Neutral/Negative to affect each other.
    const isSentimentColumn = CUSTOMER_SENTIMENT_COLUMNS.includes(this.columnName as (typeof CUSTOMER_SENTIMENT_COLUMNS)[number]);
    if (!isSentimentColumn) {
      // Sort topics to bring selected topics to the top (other multi-select columns)
      filtered.sort((a, b) => {
        if (a.tempIsSelected && !b.tempIsSelected) return -1;
        if (b.tempIsSelected && !a.tempIsSelected) return 1;
        return 0;
      });
    }

    return { topics: filtered, count: filtered.length };
  }

  /** Stable key for ngFor so each toggle stays bound to the correct option (fixes Neutral/Negative cross-talk). */
  trackByLabel(_index: number, item: { label: string; tempIsSelected: boolean }): string {
    return item.label;
  }

  get getPrimaryTopics() {
    return this.filteredTopics.topics;
  }

  get topicCount() {
    return this.filteredTopics.count;
  }
}
